import { chakra } from '@chakra-ui/react';
import { segmentMessageContent } from '../mentions/segmentMessageContent';
import type { ChatMention } from '../types';

type Props = {
  content: string;
  mentions: ChatMention[] | null | undefined;
  isOwn: boolean;
  onOpenProfile?: (userId: string) => void;
};

/**
 * A message body, with `@Name` drawn as a chip.
 *
 * Spans only, never blocks. The bubble puts the timestamp inline with the last line of
 * text via `Flex align="flex-end"`, and the `flex="0 1 auto" minW={0}` on the wrapping
 * <Text> is load-bearing for wrapping -- see the comment in MessageBubble. Anything here
 * that breaks out of inline flow breaks both.
 *
 * The chip is a span with role="button" rather than a real <button>, for the same reason:
 * a button is not inline-flow and will not wrap mid-sentence. The negative horizontal
 * margin cancels the padding so the chip occupies exactly the width its text would have
 * done, and adding a mention never reflows the rest of the line.
 */
export function MessageContent({ content, mentions, isOwn, onOpenProfile }: Props) {
  const segments = segmentMessageContent(content, mentions);

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.kind === 'text') return <span key={index}>{segment.text}</span>;

        return (
          <chakra.span
            key={index}
            role="button"
            tabIndex={0}
            fontWeight="semibold"
            borderRadius="4px"
            px="2px"
            mx="-2px"
            cursor="pointer"
            bg={isOwn ? 'whiteAlpha.300' : 'blue.50'}
            color={isOwn ? 'white' : 'blue.700'}
            _hover={{ bg: isOwn ? 'whiteAlpha.400' : 'blue.100' }}
            onClick={(e) => {
              // The bubble itself is clickable on own messages.
              e.stopPropagation();
              onOpenProfile?.(segment.userId);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              e.stopPropagation();
              onOpenProfile?.(segment.userId);
            }}
          >
            {segment.text}
          </chakra.span>
        );
      })}
    </>
  );
}
